import React from "react";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import Popover from "@material-ui/core/Popover";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import {geocodeByAddress, getLatLng} from "./utils";
import Downshift from "downshift";
import HelpToolTip from "../../components/HelpToolTip";
import Field from "../Field";
import "./style.scss";

export default class FieldLocation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      address: this.props.value,
      autocompleteItems: [],
      selected: false,
      anchorEl: null
    };

    this.debouncedFetchPredictions = debounce(this.fetchPredictions, this.props.debounce);
  }

  componentDidMount() {
    if (!window.google) {
      throw new Error(
        "Google Maps JavaScript API library must be loaded. See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library"
      );
    }

    if (!window.google.maps.places) {
      throw new Error(
        "Google Maps Places library must be loaded. Please add `libraries=places` to the src URL. See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library"
      );
    }

    this.autocompleteService = new window.google.maps.places.AutocompleteService();
    this.autocompleteOK = window.google.maps.places.PlacesServiceStatus.OK;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.state.address) {
      this.setState({address: nextProps.value});
    }
  }

  autocompleteCallback = (predictions, status) => {
    this.clearAutocomplete();
    if (status !== this.autocompleteOK) {
      if (this.props.onError) {
        this.props.onError(status);
      }
      if (this.props.clearItemsOnError) {
        this.clearAutocomplete();
      }
      this.setState({
        autocompleteItems: [
          <MenuItem disabled>
            <div>No results</div>
          </MenuItem>
        ]
      });
      return;
    }
    // transform snake_case to camelCase
    const formattedSuggestion = structuredFormatting => ({
      mainText: structuredFormatting.main_text,
      secondaryText: structuredFormatting.secondary_text
    });
    this.setState({
      autocompleteItems: predictions.map((p, idx) => {
        const {mainText, secondaryText} = formattedSuggestion(p.structured_formatting);
        if (!secondaryText) {
          return null;
        }
        const city = secondaryText.split(",")[0];
        return (
          <MenuItem key={"locationItem" + idx} onClick={e => this.handleSelect({text: p.description})}>
            <Grid container spacing={16}>
              <Grid item xs={7}>
                {mainText}
              </Grid>
              <Grid item xs={5} style={{textAlign: "right", color: "#999"}}>
                {city}
              </Grid>
            </Grid>
          </MenuItem>
        );
      })
    });
  };

  fetchPredictions = () => {
    const {address} = this.state;
    if (address.length) {
      this.autocompleteService.getPlacePredictions(
        {
          ...this.props.options,
          input: address
        },
        this.autocompleteCallback
      );
    }
  };

  clearAutocomplete() {
    this.setState({autocompleteItems: []});
  }

  selectAddress(address) {
    this.clearAutocomplete();
    this.handleSelect(address);
  }

  handleKeyUpSelect(e, mainText) {
    if (e.keyCode === 13) {
      this.handleSelect({text: mainText});
    }
  }

  handleSelect(address) {
    if (address.text) {
      this.setState({
        address: address.text,
        selected: true,
        anchorEl: null
      });
      geocodeByAddress(address.text)
        .then(results => getLatLng(results[0]))
        .then(({lat, lng}) => {
          if (this.props.addressName) {
            this.props.onChange({
              name: this.props.addressName,
              value: address.text
            });
          }
          if (this.props.geoName) {
            this.props.onChange({
              name: this.props.geoName,
              value: [lng, lat]
            });
          }
        })
        .catch(error => {
          if (this.props.onError) {
            this.props.onError(error);
          }
        });
    } else {
      this.setState({address: "", anchorEl: null});
    }
    this.clearAutocomplete();
  }

  handleInputChange = ({value, event}) => {
    if (!value) {
      this.setState({anchorEl: null, address: "", selected: false});
      return;
    }
    this.setState({anchorEl: event.target, address: value, selected: false});
    this.debouncedFetchPredictions();
  };

  handleBlur = () => {
    if (!this.state.selected) {
      if (this.props.value) {
        this.setState({address: this.props.value});
      } else {
        this.setState({address: ""});
      }
    }
  };

  handleFocus = () => {
    this.setState({selected: false});
  };

  render() {
    let {autocompleteItems, address, anchorEl} = this.state;
    let fieldProps = {...this.props};
    delete fieldProps.addressName;
    delete fieldProps.geoName;
    delete fieldProps.geoValue;
    const {helpText, successText, label, autoFocus, disabled} = fieldProps;

    return (
      <div style={{position: "relative"}}>
        {this.props.tooltip && (
          <HelpToolTip style={{position: "absolute", left: 95, top: 12}} tip={this.props.tooltip} />
        )}
        <Downshift
          onChange={selection => alert(`You selected ${selection.value}`)}
          itemToString={item => (item ? item.value : "")}>
          {({
            getInputProps,
            getItemProps,
            getLabelProps,
            getMenuProps,
            isOpen,
            inputValue,
            highlightedIndex,
            selectedItem
          }) => (
            <div>
              <Field
                id="fieldLocationInput"
                label={label}
                value={address}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleInputChange}
                autoFocus={autoFocus}
                disabled={disabled}
              />
              <Popover
                disableAutoFocus={true}
                anchorEl={anchorEl}
                style={{marginTop: "50px"}}
                open={Boolean(autocompleteItems.length && anchorEl)}
                onClose={() => {
                  this.setState({anchorEl: null});
                }}>
                {autocompleteItems.map(item => item)}
              </Popover>
            </div>
          )}
        </Downshift>
        {helpText && <span className="helpText">{helpText}</span>}
        {successText && <span className="successText">{successText}</span>}
      </div>
    );
  }
}

FieldLocation.propTypes = {
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  onChange: PropTypes.func,
  tooltip: PropTypes.string,
  helpText: PropTypes.string,
  successText: PropTypes.string
};

FieldLocation.defaultProps = {
  disabled: false
};



// WEBPACK FOOTER //
// ./src/components/FieldLocation/index.jsx